// A dashboard panel's figure (plan step 3.5, D98): a line or bar chart, one big number, a table,
// or a preset's few figures, from a run's rows (lib/panels.ts shapes them). Every value and
// column name is the app's data or an agent's words: drawn as text, never as markup, and marked
// translate="no".
import type { ReactNode } from 'react'
import { type Translator, useI18n } from '../i18n'
import { cellText, compact, duration, perMinute } from '../lib/format'
import {
  type Frame,
  isTimeType,
  type Line,
  type PanelChart,
  type PanelView,
  type Plot,
  plotOf,
  type Stat,
  type ValueFormat,
} from '../lib/panels'
import { Bars, ScrollX, Strip } from './d'
import { LineChart } from './LineChart'

/** At most this many rows in a panel's table; Explore shows them all. */
export const TABLE_ROWS = 200

/** A value in its format; a plain number takes the panel's unit. */
export function formatValue(
  i18n: Translator,
  v: number,
  format: ValueFormat,
  unit?: string,
): string {
  switch (format) {
    case 'percent':
      // As Console D writes a figure and its unit: "66.7 %".
      return `${i18n.number(v * 100, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`
    case 'ms':
      return duration(i18n, v)
    case 'perMinute':
      return perMinute(i18n, v)
    default: {
      const n =
        Math.abs(v) >= 10_000 ? compact(i18n, v) : i18n.number(v, { maximumFractionDigits: 2 })
      return unit ? (unit === '%' ? `${n}%` : `${n} ${unit}`) : n
    }
  }
}

function timeOf(v: unknown): number | null {
  if (typeof v === 'number') return v * 1000
  if (typeof v !== 'string') return null
  // ClickHouse writes `2026-10-07 12:00:00` (UTC) and `2026-10-07`.
  const iso = /^\d{4}-\d{2}-\d{2}$/.test(v)
    ? `${v}T00:00:00Z`
    : /[zZ]|[+-]\d{2}:?\d{2}$/.test(v)
      ? v.replace(' ', 'T')
      : `${v.replace(' ', 'T')}Z`
  const ms = Date.parse(iso)
  return Number.isNaN(ms) ? null : ms
}

/** The x labels: dates and times in the reader's words, anything else as text. */
function labels(i18n: Translator, plot: Plot): string[] {
  if (!isTimeType(plot.xType)) return plot.labels.map((v) => cellText(v).text)
  const times = plot.labels.map(timeOf)
  const known = times.filter((t): t is number => t !== null)
  const span = known.length ? Math.max(...known) - Math.min(...known) : 0
  const dateOnly = /^(Nullable\()?Date(32)?\)?$/.test(plot.xType)
  return plot.labels.map((v, i) => {
    const t = times[i]
    if (t === null || t === undefined) return cellText(v).text
    if (dateOnly) return i18n.date(t, { month: 'short', day: 'numeric', timeZone: 'UTC' })
    return span > 2 * 86_400_000
      ? i18n.date(t, { month: 'short', day: 'numeric' })
      : i18n.date(t, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
  })
}

const lastValue = (line: Line | undefined): number | null =>
  line?.values.findLast((v) => v !== null) ?? null

/** One panel's figure from its view. */
export function PanelFigure({
  view,
  label,
  height,
  accent = false,
}: {
  view: PanelView
  /** What the figure shows, for screen readers. */
  label: string
  height: number
  /** A line chart's last point in the accent: only for the page's one accent. */
  accent?: boolean
}) {
  const { t } = useI18n()
  if (view.kind === 'unknown') return <p className="dim">{t('dash.unknownPreset')}</p>
  if (view.kind === 'stats') return <Stats stats={view.stats} />
  const { frame, chart } = view
  if (frame.rows.length === 0) return <p className="dim">{t('dash.noRows')}</p>
  if (chart.type === 'table') return <PanelTable frame={frame} label={label} />
  const result = plotOf(frame, chart)
  if (!result.ok)
    return (
      <p className="notice">
        {result.missing ? (
          <>
            {t('dash.missingColumn')}{' '}
            <span className="mono" translate="no">
              {result.missing}
            </span>
          </>
        ) : (
          t('dash.noNumbers')
        )}
      </p>
    )
  return (
    <>
      {frame.truncated ? <p className="notice">{t('explore.truncated')}</p> : null}
      {chart.type === 'stat' ? (
        <StatFigure plot={result.plot} chart={chart} />
      ) : (
        <Chart plot={result.plot} chart={chart} label={label} height={height} accent={accent} />
      )}
    </>
  )
}

function Chart({
  plot,
  chart,
  label,
  height,
  accent,
}: {
  plot: Plot
  chart: PanelChart
  label: string
  height: number
  accent: boolean
}) {
  const i18n = useI18n()
  const format = (v: number) => formatValue(i18n, v, plot.format, chart.unit)
  const xs = labels(i18n, plot)
  const threshold =
    chart.threshold === undefined
      ? undefined
      : { value: chart.threshold, label: format(chart.threshold) }
  const [first, ...more] = plot.lines
  if (chart.type === 'bar')
    return (
      <Bars
        series={plot.lines}
        categories={xs}
        format={format}
        label={label}
        height={height}
        threshold={threshold}
        gapShare={0.3}
      />
    )
  return (
    <LineChart
      label={label}
      height={height}
      points={xs.map((l, i) => ({ label: l, value: first?.values[i] ?? null }))}
      format={format}
      name={first?.name}
      more={more}
      threshold={threshold}
      accent={accent}
    />
  )
}

/** One big number: the first value column's last value, with its unit. */
function StatFigure({ plot, chart }: { plot: Plot; chart: PanelChart }) {
  const i18n = useI18n()
  const line = plot.lines[0]
  const value = lastValue(line)
  const unit = plot.format === 'number' ? chart.unit : undefined
  const text = value === null ? '—' : formatValue(i18n, value, plot.format)
  return (
    <div className="stack">
      <p className="stat-big" translate="no">
        {text}
        {unit && value !== null ? <small>{unit}</small> : null}
      </p>
      {line ? (
        <p className="mono dim" style={{ fontSize: 12 }} translate="no">
          {line.name}
        </p>
      ) : null}
    </div>
  )
}

function Stats({ stats }: { stats: Stat[] }) {
  const i18n = useI18n()
  // Console D's figures: mono caps label, mono figure, its unit smaller beside it.
  return (
    <Strip
      translate="no"
      items={stats.map((s) => {
        const text = s.value === null ? '—' : formatValue(i18n, s.value, s.format)
        const cut = text.lastIndexOf(' ')
        return {
          dt: i18n.t(s.label),
          dd: cut > 0 ? text.slice(0, cut) : text,
          small: cut > 0 ? text.slice(cut + 1) : undefined,
        }
      })}
    />
  )
}

/** The rows as Explore shows them: text, NULL marked; formatted where a preset knows how. */
export function PanelTable({ frame, label }: { frame: Frame; label: string }) {
  const i18n = useI18n()
  const { t } = i18n
  const rows = frame.rows.slice(0, TABLE_ROWS)
  const cell = (value: unknown, format: ValueFormat | undefined): ReactNode => {
    if (format && typeof value === 'number') return formatValue(i18n, value, format)
    const { text, isNull } = cellText(value)
    return isNull ? <span className="dim">{text}</span> : text
  }
  return (
    <>
      {frame.rows.length > TABLE_ROWS || frame.truncated ? (
        <p className="note" style={{ padding: '8px 0 0' }}>
          {t('dash.firstRows', { shown: rows.length, count: frame.rowCount })}
        </p>
      ) : null}
      <ScrollX label={label}>
        <table className="t">
          <thead>
            <tr>
              {frame.columns.map((c, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: columns never reorder
                <th key={i} scope="col" translate="no">
                  <span className="mono">{c.name}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, r) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: a result's rows never reorder
              <tr key={r}>
                {frame.columns.map((c, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: cells follow the columns
                  <td key={i} className="cell-mono anywhere" translate="no">
                    {cell(row[i], c.format)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </ScrollX>
    </>
  )
}
